import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { CatalogService } from '../../core/services/catalog.service';
import { selectStoreId, selectTerminalId } from '../auth/auth.selectors';
import { CatalogActions } from './catalog.actions';

/** Fetch live category + products for the signed-in store/terminal. */
export const loadCategoryInfo$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), catalog = inject(CatalogService)) =>
    actions$.pipe(
      ofType(CatalogActions.loadCategoryInfo),
      concatLatestFrom(() => [store.select(selectStoreId), store.select(selectTerminalId)]),
      switchMap(([, storeId, terminalId]) => {
        if (!storeId || !terminalId) {
          return of(
            CatalogActions.loadCategoryInfoFailure({ error: 'Missing store/terminal id' }),
          );
        }
        return catalog.getCategoryProducts(storeId, terminalId).pipe(
          map((categories) => CatalogActions.loadCategoryInfoSuccess({ categories })),
          catchError((err) =>
            of(
              CatalogActions.loadCategoryInfoFailure({
                error: err?.error?.message ?? err?.message ?? 'Category load failed',
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);

/** Opening a product routes to its detail page. */
export const openProduct$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(CatalogActions.openProduct),
      tap(({ id }) => router.navigate(['/product', id])),
    ),
  { functional: true, dispatch: false },
);
